import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/client';
import { createWazeRefresh } from '../api/wazeRefresh';

export default function useWazeAlerts(user) {
  const token = localStorage.getItem('rv_token');
  const owner = user?.id && token ? `${user.id}:${token}` : null;
  const ownerRef = useRef(owner);
  ownerRef.current = owner;
  const [state, setState] = useState({ owner: null, items: [], loading: false, error: '' });
  const controller = useMemo(() => {
    let disposed = false;
    const currentSession = () => !disposed && owner && ownerRef.current === owner &&
      localStorage.getItem('rv_token') === token ? owner : null;
    const refresh = createWazeRefresh({ api, currentSession,
      apply: items => setState({ owner, items, loading: false, error: '' }) });
    return {
      activate: () => { disposed = false; },
      dispose: () => { disposed = true; },
      async fetch() {
        if (!currentSession()) return;
        setState(previous => ({ owner, items: previous.owner === owner ? previous.items : [], loading: true, error: '' }));
        try { await refresh(); }
        catch (error) {
          if (currentSession()) setState(previous => ({ ...previous, owner, error: error.message || 'Unable to refresh Waze alerts.' }));
        } finally {
          if (currentSession()) setState(previous => ({ ...previous, loading: false }));
        }
      },
    };
  }, [owner, token]);
  useEffect(() => { controller.activate(); return () => controller.dispose(); }, [controller]);
  const fetchAlerts = useCallback(() => controller.fetch(), [controller]);
  const clearAlerts = useCallback(() => setState({ owner, items: [], loading: false, error: '' }), [owner]);
  return { alerts: state.owner === owner ? state.items : [], loading: state.owner === owner && state.loading,
    error: state.owner === owner ? state.error : '', fetchAlerts, clearAlerts };
}
